import { useCallback, useEffect, useState } from 'react';

// Hooks
import useDebounce from '../../../apps/football/src/hooks/useDebounce';
import useFetchIntervalData from '../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../packages/hooks/useSubscriptionClick';

// Utils
import dayjs from 'dayjs';
import { normalizeEventsData } from '../../normalizers/src/multi-sport/normalizeEventsResponseData';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { getMultiSportEvents } from '../../services/multi-sport/events';

// Constants
import { ALL } from '../../../packages/constants/statusFilters';
import { IGeneralSubscriberInfo } from '../../../packages/constants/subscribeTypes';

// Types
import { IMultiSportLivescoreConfig, IMultiSportLivescoreDataLabels, INormalizedEvents } from '../../types/multisport';
import { IConfigEntityLinks, SDKOptions, ShortNameType } from '../../../packages/types';

interface IUseEventsArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: IMultiSportLivescoreConfig['dataRefreshTime'];
  statusFilter?: string;
  date: Date;
  dataCompetitions?: IMultiSportLivescoreConfig['dataCompetitions'];
  dataOddsDisplay: IMultiSportLivescoreConfig['dataOddsDisplay'];
  dataOddsMarketValueType: IMultiSportLivescoreConfig['dataOddsMarketValueType'];
  dataOddsBettingId: IMultiSportLivescoreConfig['dataOddsBettingId'];
  dataOddsMarket: IMultiSportLivescoreConfig['dataOddsMarket'];
  dataOddsPreEventOnly: IMultiSportLivescoreConfig['dataOddsPreEventOnly'];
  sdkOptions: SDKOptions;
  dataTeamNameType?: ShortNameType | null;
  dataLabels?: IMultiSportLivescoreDataLabels | null;
  dataEntityLinks?: IConfigEntityLinks | null;
  dataPopularList?: string;
  utcOffset: string;
}

export const useEvents = ({
  subscribers,
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  dataOddsDisplay,
  dataOddsMarketValueType,
  dataOddsPreEventOnly,
  dataOddsBettingId,
  dataOddsMarket,
  statusFilter,
  date,
  dataCompetitions,
  dataWidgetCuid,
  sdkOptions,
  dataTeamNameType,
  dataLabels,
  dataEntityLinks,
  dataPopularList,
  utcOffset,
}: IUseEventsArgs) => {
  const debouncedDateFilter = useDebounce(date, 300);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [livescore, setLivescore] = useState<INormalizedEvents[]>([]);

  const params = {
    date: dayjs(debouncedDateFilter).format('YYYY-MM-DD'),
    statusFilter: statusFilter ?? ALL,
    dataPopularList: dataPopularList,
    utcOffset: utcOffset,
    competitionIds: dataCompetitions,
    sport: dataWidgetSport,
    dataOddsDisplay: dataOddsDisplay,
    dataOddsBettingId: dataOddsBettingId,
    dataOddsMarket: dataOddsMarket,
    dataOddsMarketValueType: dataOddsMarketValueType,
    dataOddsPreEventOnly: dataOddsPreEventOnly,
  };

  const fetchGames = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      manageLoader && setIsLoading(true);
      getMultiSportEvents({ params, sdkOptions })
        ?.then(({ competitions }) => {
          manageLoader && setIsLoading(false);
          const livescore = normalizeEventsData({
            competitions: competitions,
            nameType: dataTeamNameType,
            dataLabels: dataLabels,
            dataEntityLinks: dataEntityLinks,
            sdkOptions,
          });
          setLivescore(livescore);
          !isFromInterval && stackOdds(competitions, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
        })
        .catch((error: unknown) => {
          console.error(error);
        });
    },
    [
      dataOddsBettingId,
      dataOddsDisplay,
      dataOddsMarket,
      dataOddsMarketValueType,
      dataOddsPreEventOnly,
      debouncedDateFilter,
      dataTeamNameType,
      statusFilter,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
      dataCompetitions,
    ],
  );

  useFetchIntervalData(fetchGames, dataRefreshTime);

  useEffect(() => {
    fetchGames(true);
  }, [fetchGames]);

  return {
    widgetData: { livescore, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
};
